import { Button, IconButton, Stack, useTheme } from '@mui/material'
import MaterialReceptionHeader from './MaterialReceptionHeader'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import AlertUI from '../generic/AlertUI'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useEffect, useState } from 'react'
import { apiPut, apiGet } from '../generic/Api_Functions'
import SmartTextField from '../generic/smarttextfield/SmartTextField'
import { InfoOutlined, TextSnippetOutlined } from '@mui/icons-material'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { DefaultAccordion } from '../generic/DefaultAccordion'
import Document from '../configuration/document/Document'
import { useNavigate } from 'react-router-dom'
import WarningIcon from '@mui/icons-material/Warning'
import { checkQrRequirementsColor, updateQr } from './QR_utils'
import createDialogTitle from '../generic/utilities/dialogUtil'
import Requirement from './Requirement'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { getMaterialReceptionColumns } from './MaterialReceptionExtendedColumnsUtil'
import SmartSelect from '../generic/smartSelect/SmartSelect'

/**
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNoteDataAction} DeliveryNoteDataAction DataAction object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} DeliveryNote DeliveryNote object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').PurchaseOrderReception} PurchaseOrderReception PurchaseOrderReception object
 */

/**
 *
 * @param {object} props
 * @param {DeliveryNote} props.deliveryNote
 * @param {React.Dispatch<DeliveryNoteDataAction>} props.dispatchDeliveryNote
 * @param {PurchaseOrderReception[]} props.allReceptions
 * @param {string} props.lastQrVersion
 * @param {import('../generic/utilities/objectStructures/materialreception/materialReception').Requirement[]} props.requirements
 * @returns {JSX.Element}
 */
const QualityReportQuantity = ({ deliveryNote, dispatchDeliveryNote, allReceptions, lastQrVersion, requirements, rawMaterialExtendedColumns }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  /** @type {[PurchaseOrderReception[], React.Dispatch<SetStateAction<PurchaseOrderReception[]>>]} */
  const [reportedData, setReportedData] = useState(allReceptions)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [realRow, setRealRow] = useState(null)
  const [theoreticalRow, setTheoreticalRow] = useState(null)
  const isUserAuthorized = useAuthorization()

  const [currentSelectedRow, setCurrentSelectedRow] = useState()
  const [currentSelectedRequirement, setCurrentSelectedRequirement] = useState()
  const [openRequirementDialog, setOpenRequirementDialog] = useState(false)
  const [storehouses, setStorehouses] = useState([])

  const theme = useTheme()
  const style = {
    minWidth: '80rem',
    width: '100%',
    display: 'grid',
    gap: '1rem',
    backgroundColor: theme.palette.background.row2,
    padding: '1rem'
  }

  useEffect(() => {
    setReportedData(allReceptions)
  }, [deliveryNote?.id, allReceptions])

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet('storehouses')
        setStorehouses(data || [])
      } catch (err) {
        console.error('failed to fetch storehouses', err)
        setStorehouses([])
      }
    }
    load()
  }, [])

  const navigate = useNavigate()
  const columns = [
    {
      name: t_('Status'),
      field: 'status',
      render: (row) => systemValueLabel(t_, 'PurchaseOrderTheoreticalReceptionStatusEnum', row.status),
      filterByLabel: { options: enumFilterOptions(t_, 'PurchaseOrderTheoreticalReceptionStatusEnum') }
    },
    {
      name: t_('Supplier code'),
      field: 'supplier.code'
    },
    {
      name: t_('Supplier name'),
      field: 'supplier.name'
    },
    {
      name: t_('Record code'),
      field: 'recordCode'
    },
    {
      name: t_('Record position'),
      field: 'recordPos'
    },
    {
      name: t_('Order'),
      field: 'order'
    },
    {
      name: t_('Pos / Line'),
      field: 'lineOrderPosition'
    },
    {
      name: t_('Raw material reference'),
      field: 'rawMaterialReference'
    },
    {
      name: t_('Revision'),
      field: 'revision'
    },
    {
      name: t_('Batch'),
      field: 'batchNumber'
    },
    {
      name: t_('Project'),
      field: 'projectCode',
      readOnly: true
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      csvHeader: t_('QR'),
      name:
  <>
    {t_('QR')}
    <a
      href={'/GDELS/requirements/requirements_' + lastQrVersion + '.pdf'}
      download={'requirements_' + lastQrVersion + '.pdf'}
    >
      <IconButton onClick={(event) => { event.stopPropagation() }}>
        <InfoOutlined />
      </IconButton>
    </a>
  </>,
      field: 'purchaseOrderQrValue',
      render: (row) => {
        return (
          <Button
            sx={{ borderWidth: '4px', '&:hover': { borderWidth: '4px' } }}
            onClick={() => {
              const find = reportedData.find(data => data.id === row.id)
              let qrVersion = lastQrVersion
              if (row.status !== 'FINISHED') {
                qrVersion = row.qr.split(':')[0]
              }
              const requirement = requirements.find(req => req.fileVersion === qrVersion)
              setCurrentSelectedRequirement(requirement)
              setCurrentSelectedRow({ ...find })
              setOpenRequirementDialog(true)
            }}
            variant='outlined'
            color={checkQrRequirementsColor(deliveryNote?.id ? reportedData?.find(data => data.id === row.id)?.qr : row?.qr, row?.purchaseOrderQrValue?.split(''))}
            disabled={!deliveryNote?.id}
          >
            {row.purchaseOrderQrValue}
          </Button>
        )
      }
    },
    {
      name: t_('Received quantity'),
      field: 'receivedQuantityView'
    },
    {
      name: t_('Approved quantity'),
      field: 'approvedQuantity',
      render: (row) => {
        if (!deliveryNote?.id) {
          return row.approvedQuantity
        }

        let result = reportedData.find(data => data.id === row.id)?.approvedQuantity
        if (result === null) {
          result = ''
        }
        return (
          <SmartTextField
            value={result}
            onChange={(value) => {
              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].approvedQuantity = value
              setReportedData(newData)
            }}
            checkError={(value) => {
              const find = reportedData.find(data => data.id === row.id)
              if (!value && row.status !== 'QUALITY' && row.status !== 'FINISHED') {
                return ''
              }

              if (!value && value !== 0) {
                return t_('Quantity must not be empty')
              }

              if (isNaN(value) || Number(value) < 0) {
                return t_('Quantity must be a number greater or equal 0')
              }
              if (Number(value) + Number(find.rejectedQuantity) !== Number(find.receivedQuantity)) {
                return t_('Quantity sum must be equal to the received quantity')
              }

              return ''
            }}
            disabled={row.locked}
            checkAuto
          />
        )
      }
    },
    {
      name: t_('Rejected quantity'),
      field: 'rejectedQuantity',
      render: (row) => {
        if (!deliveryNote?.id) {
          return row.rejectedQuantity
        }
        let result = reportedData.find(data => data.id === row.id)?.rejectedQuantity
        if (result === null) {
          result = ''
        }
        return (
          <SmartTextField
            value={result}
            onChange={(value) => {
              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].rejectedQuantity = value
              setReportedData(newData)
            }}
            checkError={(value) => {
              const find = reportedData.find(data => data.id === row.id)
              if (!value && row.status !== 'QUALITY' && row.status !== 'FINISHED') {
                return ''
              }

              if (!value && value !== 0) {
                return t_('Quantity must not be empty')
              }

              if (isNaN(value) || Number(value) < 0) {
                return t_('Quantity must be a number greater or equal 0')
              }
              if (Number(value) + Number(find.approvedQuantity) !== Number(find.receivedQuantity)) {
                return t_('Quantity sum must be equal to the received quantity')
              }

              return ''
            }}
            disabled={row.locked}
            checkAuto
          />
        )
      }
    },
    {
      name: t_('Storehouse'),
      field: 'storehouse.name',
      type: 'smartSelect',
      render: (row) => {
        if (!deliveryNote?.id) {
          return row.storehouse?.name || ''
        }
        return (
          <SmartSelect
            value={row.storehouse?.name || null}
            onChange={(value) => {
              const selected = value != null ? storehouses.find(s => s.name === value) : null
              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].storehouse = selected
              setReportedData(newData)
            }}
            selectableOptions={storehouses.map(s => s.name)}
            disabled={row.locked}
            autoSelectFirst={false}
            disableClearable={false}
          />
        )
      }
    }
  ]

  const onSave = async (number, date) => {
    let correctFormat = true
    for (const element of reportedData) {
      if (!element.approvedQuantity && !element.rejectedQuantity && element.status !== 'QUALITY') {
        continue
      }

      if ((!element.approvedQuantity && element.approvedQuantity !== 0) || (!element.rejectedQuantity && element.rejectedQuantity !== 0)) {
        showAlert({ severity: 'error', title: t_('There are some incorrect values. Fix them before saving') })
        correctFormat = false
        return
      }

      if ((isNaN(element.approvedQuantity) || Number(element.approvedQuantity < 0)) || (isNaN(element.rejectedQuantity) || Number(element.rejectedQuantity < 0))) {
        showAlert({ severity: 'error', title: t_('There are some incorrect values. Fix them before saving') })
        correctFormat = false
        return
      }

      if (Number(element.receivedQuantity) !== (Number(element.approvedQuantity) + Number(element.rejectedQuantity))) {
        showAlert({ severity: 'error', title: t_('There are some incorrect values. Fix them before saving') })
        correctFormat = false
        return
      }
    }

    if (!correctFormat) {
      return false
    }

    const data = { ...deliveryNote }
    data.date = date
    data.number = number

    data.purchaseOrderTheoreticalReceptions.forEach(purchaseOrderTheoreticalReception => {
      const find = reportedData.filter(data => data.purchaseOrderTheoreticalReceptionId === purchaseOrderTheoreticalReception.id)
      if (find.length > 0) {
        purchaseOrderTheoreticalReception.purchaseOrderReceptions = find
      }
    })
    const result = await apiPut('deliveryNote/update/' + lastQrVersion + '/' + true, data)
    dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: result })
    const data2 = []
    result.purchaseOrderTheoreticalReceptions.forEach((purchaseOrderTheoreticalReception) => {
      if (purchaseOrderTheoreticalReception.purchaseOrderReceptions) {
        purchaseOrderTheoreticalReception.purchaseOrderReceptions.forEach((reception) => {
          if (reception.receivedQuantity != null && ((!reception.serializable) || (reception.serializable && reception.batchNumber !== null))) {
            data2.push(reception)
          }
        })
      }
    })
    setReportedData(data2)
    return true
  }

  const actions = {
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          const realData = { id: row.id, name: 'Position: ' + row.recordPos + '  ||   Order: ' + row.order + '  ||   Line: ' + row.lineOrderPosition }
          setRealRow(realData)
          const theoreticalData = { id: row.purchaseOrderTheoreticalReceptionId, name: 'Order: ' + row.order + '  ||   Line: ' + row.lineOrderPosition }
          setTheoreticalRow(theoreticalData)
          setShowDocument(true)
        },
        disabled: !isUserAuthorized('DOCUMENTS_PURCHASE_ORDER_QUALITATIVE_RECEPTION')
      }, {
        name: 'Incidence',
        render: (row) => {
          return (
            <IconButton>
              <WarningIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          navigate('/mos/reception/incidence', { state: { deliveryNote, type: 'QUALITY/QUANTITY', currentSelectedId: row.id, window: 'QUALITY_QUANTITY' } })
        },
        disabled: (row) => !isUserAuthorized('RECEPTION_INCIDENCE') || row.type === 'MANUAL'
      }
    ]
  }

  const renderAccordion = (header, reference, defaultExpanded, resourceType) => {
    return (
      <DefaultAccordion headerRender={t_(header)} defaultExpanded={defaultExpanded} disabled={!isUserAuthorized('DOCUMENTS_' + resourceType)}>
        <Document reference={reference} resourceType={resourceType} isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
      </DefaultAccordion>
    )
  }
  const getColumns = () =>
    getMaterialReceptionColumns({
      columns,
      extendedColumns: rawMaterialExtendedColumns,
      editIndex: null
    })
  const dataFetch = async (requestPagination) => {
    if (deliveryNote?.id) {
      return allReceptions
    }
    return apiPut('purchaseOrderReception/getAllQuality', requestPagination)
  }

  return (
    <Stack>
      <SmartDialog
        setOpen={openRequirementDialog}
        cancelCallback={() => setOpenRequirementDialog(false)}
        acceptCallback={() => {
          setReportedData(prevData =>
            prevData.map(row =>
              row.id === currentSelectedRow.id ? { ...row, qr: currentSelectedRow.qr } : row
            )
          )
          setOpenRequirementDialog(false)
        }}
        title={createDialogTitle({ color: 'primary', title: t_('Requirements'), icon: FormatAlignJustifyIcon })}
        renderComponent={
          <Requirement
            requirement={currentSelectedRequirement}
            qr={currentSelectedRow?.qr?.split(':')[1].split(';')}
            purchaseOrderQrValue={currentSelectedRow?.purchaseOrderQrValue?.split('')}
            setQr={(newValue, index) => {
              if (!currentSelectedRow) {
                return
              }
              const updatedQr = updateQr(currentSelectedRow.qr, newValue, index)
              const updatedRow = { ...currentSelectedRow, qr: updatedQr }
              setCurrentSelectedRow(updatedRow)
              setReportedData(prevData =>
                prevData.map(row =>
                  row.id === updatedRow.id ? { ...row, qr: updatedQr } : row
                )
              )
            }}
            locked={currentSelectedRow?.locked}
          />
        }
      />
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <div style={style}>
            {renderAccordion(t_('Delivery note'), { id: deliveryNote?.id, name: deliveryNote?.number }, false, 'DELIVERY_NOTE')}
            {renderAccordion(t_('Theoretical quantity'), theoreticalRow, false, 'PURCHASE_ORDER_THEORETICAL_RECEPTION')}
            {renderAccordion(t_('Real quantity'), realRow, false, 'PURCHASE_ORDER_REAL_RECEPTION')}
            {renderAccordion(t_('Qualitative quantity'), realRow, true, 'PURCHASE_ORDER_QUALITATIVE_RECEPTION')}
          </div>
              }
        setOpen={showDocument}
      />
      {AlertElement}
      <MaterialReceptionHeader
        deliveryNote={deliveryNote}
        dispatchDeliveryNote={dispatchDeliveryNote}
        showAlert={showAlert}
        onSave={(number, date) => onSave(number, date)}
        window='QUALITY_QUANTITY'
      />
      <div style={{ marginTop: '1rem' }}>
        <SmartMateriaUITable
          tableTuneUniqueKey='ReportQuantity'
          columns={getColumns()}
          dataFetch={dataFetch}
          actions={actions}
          pageable={deliveryNote?.id ? undefined : true}
          stickyTopOffset={112}
        />
      </div>
    </Stack>
  )
}

export default QualityReportQuantity
